import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Save, Clock, RotateCcw, ArrowLeft, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChartConfiguration {
  hourlyValues: number[];
}

interface TimeBlock {
  id: number;
  time: string;
  value: number;
}

interface ChartData {
  title: string;
  timeBlocks: TimeBlock[];
}

const ChartConfigPage: React.FC = () => {
  const navigate = useNavigate();
  // Generate 24 hourly time blocks (00:00 to 23:00)
  const generateTimeBlocks = (): TimeBlock[] => {
    const blocks: TimeBlock[] = [];

    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, '0');
      blocks.push({
        id: i,
        time: `${hour}:00`,
        value: 0, // Start with 0 values
      });
    }

    return blocks;
  };

  const [activeTab, setActiveTab] = useState<string>('ΜΥΗΣ Προεσπέρας');

  const [chartData, setChartData] = useState<Record<string, TimeBlock[]>>({
    'ΜΥΗΣ Προεσπέρας': generateTimeBlocks(),
    'ΜΥΗΣ Κάτω Προεσπέρας': generateTimeBlocks(),
    'Δήλωση Υβριδικής Α&Β': generateTimeBlocks(),
  });

  const tabs = [
    { id: 'ΜΥΗΣ Προεσπέρας', label: 'ΜΥΗΣ Προεσπέρας' },
    { id: 'ΜΥΗΣ Κάτω Προεσπέρας', label: 'ΜΥΗΣ Κάτω Προεσπέρας' },
    { id: 'Δήλωση Υβριδικής Α&Β', label: 'Δήλωση Υβριδικής Α&Β' },
  ];

  // Function to get time-based gradient colors
  const getTimeGradient = (hour: number) => {
    if (hour >= 0 && hour <= 5) {
      // Night - Deep blue gradient
      return {
        bg: 'bg-gradient-to-br from-blue-800 to-indigo-900',
        text: 'text-blue-100',
        border: 'border-blue-700',
        cardBg: 'bg-gradient-to-r from-blue-50/30 to-indigo-50/20',
      };
    } else if (hour >= 6 && hour <= 8) {
      // Early morning - Light blue/cyan gradient
      return {
        bg: 'bg-gradient-to-br from-cyan-600 to-blue-600',
        text: 'text-cyan-100',
        border: 'border-cyan-500',
        cardBg: 'bg-gradient-to-r from-cyan-50/30 to-blue-50/20',
      };
    } else if (hour >= 9 && hour <= 11) {
      // Morning - Yellow/orange gradient
      return {
        bg: 'bg-gradient-to-br from-yellow-500 to-orange-500',
        text: 'text-yellow-100',
        border: 'border-yellow-400',
        cardBg: 'bg-gradient-to-r from-yellow-50/30 to-orange-50/20',
      };
    } else if (hour >= 12 && hour <= 14) {
      // Noon - Orange gradient
      return {
        bg: 'bg-gradient-to-br from-orange-500 to-red-500',
        text: 'text-orange-100',
        border: 'border-orange-400',
        cardBg: 'bg-gradient-to-r from-orange-50/30 to-red-50/20',
      };
    } else if (hour >= 15 && hour <= 17) {
      // Afternoon - Red gradient
      return {
        bg: 'bg-gradient-to-br from-red-500 to-pink-600',
        text: 'text-red-100',
        border: 'border-red-400',
        cardBg: 'bg-gradient-to-r from-red-50/30 to-pink-50/20',
      };
    } else if (hour >= 18 && hour <= 20) {
      // Evening - Purple gradient
      return {
        bg: 'bg-gradient-to-br from-purple-600 to-violet-700',
        text: 'text-purple-100',
        border: 'border-purple-500',
        cardBg: 'bg-gradient-to-r from-purple-50/30 to-violet-50/20',
      };
    } else {
      // Late evening/night - Deep purple gradient
      return {
        bg: 'bg-gradient-to-br from-purple-800 to-indigo-900',
        text: 'text-purple-100',
        border: 'border-purple-700',
        cardBg: 'bg-gradient-to-r from-purple-50/30 to-indigo-50/20',
      };
    }
  };

  // Get time period label
  const getTimePeriod = (hour: number) => {
    if (hour >= 0 && hour <= 5) return 'Νύχτα';
    if (hour >= 6 && hour <= 8) return 'Πρωί';
    if (hour >= 9 && hour <= 11) return 'Πρωινό';
    if (hour >= 12 && hour <= 14) return 'Μεσημέρι';
    if (hour >= 15 && hour <= 17) return 'Απόγευμα';
    if (hour >= 18 && hour <= 20) return 'Βράδυ';
    return 'Νύχτα';
  };

  const handleSave = () => {
    const config: Record<string, ChartConfiguration> = {};

    Object.entries(chartData).forEach(([chartTitle, timeBlocks]) => {
      config[chartTitle] = {
        hourlyValues: timeBlocks.map((block) => block.value),
      };
    });

    // Here you can handle the save logic (e.g., API call, local storage, etc.)
    console.log('All chart configurations saved:', config);

    // Navigate back to calculate page - replace with your navigation logic
    console.log('Navigating back to calculate page');
  };

  const updateBlockValue = (chartTitle: string, blockId: number, newValue: number) => {
    setChartData((prev) => ({
      ...prev,
      [chartTitle]: prev[chartTitle].map((block) => (block.id === blockId ? { ...block, value: newValue } : block)),
    }));
  };

  const resetAllValues = (chartTitle: string) => {
    setChartData((prev) => ({
      ...prev,
      [chartTitle]: prev[chartTitle].map((block) => ({
        ...block,
        value: 0,
      })),
    }));
  };

  const handleGoBack = () => {
    navigate('/naeras/hybrid');
    console.log('Navigating back to calculate page');
  };

  const currentTimeBlocks = chartData[activeTab];
  const morningHours = currentTimeBlocks.slice(0, 12); // Hours 0-11
  const afternoonHours = currentTimeBlocks.slice(12, 24); // Hours 12-23

  const TimeBlockComponent = ({
    block,
    index,
    columnDelay = 0,
  }: {
    block: TimeBlock;
    index: number;
    columnDelay?: number;
  }) => {
    const timeGradient = getTimeGradient(block.id);
    const timePeriod = getTimePeriod(block.id);

    return (
      <motion.div
        key={block.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: (index + columnDelay) * 0.01 }}
        className="group"
      >
        <div
          className={`flex items-center gap-3 rounded-lg border border-gray-200 ${timeGradient.cardBg} p-3 shadow-sm transition-all hover:border-blue-300 hover:shadow-md`}
        >
          {/* Time Display */}
          <div className="flex min-w-[70px] items-center gap-2">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-md ${timeGradient.bg} ${timeGradient.text}`}
            >
              <span className="text-xs font-bold">{block.id}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-bold text-gray-900">{block.time}</span>
              <span className="text-xs text-gray-500">{timePeriod}</span>
            </div>
          </div>

          {/* Value Input */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="number"
                value={block.value}
                onChange={(e) => updateBlockValue(activeTab, block.id, parseFloat(e.target.value) || 0)}
                placeholder="0.0"
                className="w-full [appearance:textfield] rounded-md border border-gray-300 bg-white/80 px-3 py-2 pr-12 text-right text-sm font-semibold text-gray-900 transition-all group-hover:border-blue-400 focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500/20 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                min="0"
                max="1000"
                step="0.1"
              />
              <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                <span className="text-xs font-medium text-gray-400">MWh</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-3">
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Ρύθμιση Γραφημάτων</h1>
                <p className="text-base text-gray-600">Διαμόρφωση ωριαίων τιμών για όλα τα γραφήματα</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/80 px-4 py-2.5 text-sm font-medium text-gray-700 shadow-lg shadow-gray-900/5 backdrop-blur-sm transition-all duration-200 hover:bg-white hover:shadow-xl hover:shadow-gray-900/10"
              >
                <ArrowLeft className="h-4 w-4" />
                Επιστροφή στην Ανάλυση
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-blue-500/25 transition-all duration-200 hover:from-blue-600 hover:to-blue-700 hover:shadow-xl hover:shadow-blue-500/30"
              >
                <Save className="h-4 w-4" />
                Αποθήκευση Όλων
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-xl backdrop-blur-sm">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-6 py-4 text-center font-medium transition-all ${
                    activeTab === tab.id
                      ? 'border-b-2 border-blue-500 bg-blue-50/50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Section Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-3">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Ωριαία Δεδομένα - {activeTab}</h3>
                  <p className="text-sm text-gray-500">Εισάγετε τιμές για κάθε ώρα</p>
                </div>
              </div>
              <button
                onClick={() => resetAllValues(activeTab)}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50"
              >
                <RotateCcw className="h-4 w-4" />
                Επαναφορά
              </button>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Morning Hours Column (0-11) */}
              <div className="space-y-3">
                <div className="mb-4 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-600 to-yellow-500"></div>
                  <h4 className="text-lg font-semibold text-gray-800">Πρωινές Ώρες (00:00 - 11:00)</h4>
                </div>
                <div className="space-y-2">
                  {morningHours.map((block, index) => (
                    <TimeBlockComponent key={block.id} block={block} index={index} columnDelay={0} />
                  ))}
                </div>
              </div>

              {/* Afternoon/Evening Hours Column (12-23) */}
              <div className="space-y-3">
                <div className="mb-4 flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-purple-600"></div>
                  <h4 className="text-lg font-semibold text-gray-800">Απογευματινές Ώρες (12:00 - 23:00)</h4>
                </div>
                <div className="space-y-2">
                  {afternoonHours.map((block, index) => (
                    <TimeBlockComponent key={block.id} block={block} index={index} columnDelay={12} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartConfigPage;
